import React, { useCallback, useEffect, useRef, useState } from 'react';
import './AdminContent.css';

const API = 'http://localhost:5001/api/content';

// ── CHIFFRES ──────────────────────────────────────────────────────────────────

// Converts API items (value as number) to form items (value as string) for free-form editing
const toForm = (items) => items.map(it => ({ ...it, value: String(it.value ?? '') }));
const fromForm = (items) => items.map(it => ({ ...it, value: parseInt(it.value, 10) || 0 }));

function ChiffresManager() {
  const [top, setTop] = useState([
    { value: '5000', suffix: '+', label: 'Références produits' },
    { value: '2',    suffix: '',  label: 'Continents desservis' },
    { value: '100',  suffix: '%', label: 'Satisfaction client'  },
  ]);
  const [bot, setBot] = useState([
    { label: 'Distribution alimentaire\n& non-alimentaire' },
    { label: 'Import & Export\ninternational' },
    { label: 'Sourcing &\nNégociation' },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: true });

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg({ text: '', ok: true }), 3500);
  };

  useEffect(() => {
    fetch(`${API}/chiffres`)
      .then(r => r.json())
      .then(d => {
        if (d.chiffresTop?.length) setTop(toForm(d.chiffresTop));
        if (d.chiffresBot?.length) setBot(d.chiffresBot);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const changeTop = (i, field, val) => {
    if (field === 'value') {
      // Only allow digits (and empty string to let the user clear the field)
      if (val !== '' && !/^\d+$/.test(val)) return;
    }
    setTop(prev => prev.map((item, idx) =>
      idx === i ? { ...item, [field]: val } : item
    ));
  };

  const changeBot = (i, val) =>
    setBot(prev => prev.map((item, idx) => idx === i ? { ...item, label: val } : item));

  const addTop = () => setTop(prev => [...prev, { value: '', suffix: '', label: 'Nouveau chiffre' }]);
  const removeTop = (i) => setTop(prev => prev.filter((_, idx) => idx !== i));
  const addBot = () => setBot(prev => [...prev, { label: 'Nouveau domaine' }]);
  const removeBot = (i) => setBot(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/chiffres`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chiffresTop: fromForm(top), chiffresBot: bot }),
      });
      if (!res.ok) throw new Error();
      flash('Chiffres enregistrés avec succès.', true);
    } catch {
      flash('Erreur lors de la sauvegarde.', false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="ac-loading">Chargement…</p>;

  return (
    <div className="ac-section">
      <div className="ac-section__header">
        <div>
          <h3>GSSG en quelques chiffres</h3>
          <p>Modifiez les statistiques affichées dans la rubrique chiffres clés du site.</p>
        </div>
        <button className="ac-btn ac-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {msg.text && (
        <p className={`ac-msg ${msg.ok ? 'ac-msg--ok' : 'ac-msg--err'}`}>{msg.text}</p>
      )}

      {/* Chiffres avec compteur */}
      <div className="ac-card">
        <div className="ac-card__head">
          <span className="ac-card__label">Chiffres avec compteur animé</span>
          <button className="ac-btn ac-btn--ghost ac-btn--sm" onClick={addTop}>+ Ajouter</button>
        </div>
        <div className="ac-chiffres-grid">
          {top.map((item, i) => (
            <div key={i} className="ac-chiffre-row">
              <label className="ac-label">Valeur</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="ac-input ac-input--sm"
                value={item.value}
                placeholder="0"
                onChange={e => changeTop(i, 'value', e.target.value)}
              />
              <label className="ac-label">Suffixe</label>
              <input
                type="text"
                className="ac-input ac-input--xs"
                placeholder="+ % h …"
                value={item.suffix}
                onChange={e => changeTop(i, 'suffix', e.target.value)}
              />
              <label className="ac-label">Libellé</label>
              <input
                type="text"
                className="ac-input ac-input--grow"
                value={item.label}
                onChange={e => changeTop(i, 'label', e.target.value)}
              />
              <button
                className="ac-btn ac-btn--danger ac-btn--icon"
                onClick={() => removeTop(i)}
                title="Supprimer"
              >✕</button>
            </div>
          ))}
        </div>
      </div>

      {/* Chiffres sans compteur */}
      <div className="ac-card">
        <div className="ac-card__head">
          <span className="ac-card__label">Domaines d'activité (sans compteur)</span>
          <button className="ac-btn ac-btn--ghost ac-btn--sm" onClick={addBot}>+ Ajouter</button>
        </div>
        <div className="ac-chiffres-grid">
          {bot.map((item, i) => (
            <div key={i} className="ac-chiffre-row">
              <label className="ac-label">Libellé</label>
              <textarea
                className="ac-input ac-input--grow ac-textarea"
                value={item.label}
                rows={2}
                onChange={e => changeBot(i, e.target.value)}
              />
              <button
                className="ac-btn ac-btn--danger ac-btn--icon"
                onClick={() => removeBot(i)}
                title="Supprimer"
              >✕</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── LOGOS CLIENTS ─────────────────────────────────────────────────────────────

function LogosManager() {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newName, setNewName] = useState('');
  const [preview, setPreview] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [msg, setMsg] = useState({ text: '', ok: true });
  const fileRef = useRef(null);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg({ text: '', ok: true }), 3500);
  };

  const fetchLogos = useCallback(() => {
    setLoading(true);
    fetch(`${API}/logos`)
      .then(r => r.json())
      .then(setLogos)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLogos(); }, [fetchLogos]);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      flash('Image trop volumineuse (max 5 Mo).', false);
      return;
    }
    setNewName(file.name.replace(/\.[^.]+$/, ''));
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFileData(ev.target.result);
      setPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!fileData) { flash('Sélectionnez une image d\'abord.', false); return; }
    setUploading(true);
    try {
      const res = await fetch(`${API}/logos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName || 'Logo client', data: fileData }),
      });
      if (!res.ok) throw new Error();
      flash('Logo ajouté avec succès.', true);
      setFileData(null);
      setPreview(null);
      setNewName('');
      if (fileRef.current) fileRef.current.value = '';
      fetchLogos();
    } catch {
      flash('Erreur lors de l\'ajout du logo.', false);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce logo ?')) return;
    try {
      const res = await fetch(`${API}/logos/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      flash('Logo supprimé.', true);
      setLogos(prev => prev.filter(l => l._id !== id));
    } catch {
      flash('Erreur lors de la suppression.', false);
    }
  };

  return (
    <div className="ac-section">
      <div className="ac-section__header">
        <div>
          <h3>Logos clients — Ils nous font confiance</h3>
          <p>Ajoutez ou supprimez les logos affichés dans le carrousel clients.</p>
        </div>
      </div>

      {msg.text && (
        <p className={`ac-msg ${msg.ok ? 'ac-msg--ok' : 'ac-msg--err'}`}>{msg.text}</p>
      )}

      {/* Upload */}
      <div className="ac-card ac-upload-card">
        <span className="ac-card__label">Ajouter un logo</span>
        <div className="ac-upload-row">
          {preview && (
            <div className="ac-upload-preview">
              <img src={preview} alt="Prévisualisation" />
            </div>
          )}
          <div className="ac-upload-fields">
            <label className="ac-label">Fichier image (PNG, JPG, SVG — max 5 Mo)</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="ac-file-input"
              onChange={onFileChange}
            />
            <label className="ac-label" style={{ marginTop: 10 }}>Nom affiché (facultatif)</label>
            <input
              type="text"
              className="ac-input"
              placeholder="Ex : Client Dupont"
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <button
              className="ac-btn ac-btn--primary"
              style={{ marginTop: 14, alignSelf: 'flex-start' }}
              onClick={upload}
              disabled={uploading || !fileData}
            >
              {uploading ? 'Envoi en cours…' : '⬆ Téléverser le logo'}
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="ac-card">
        <span className="ac-card__label">
          Logos actuels ({logos.length})
        </span>
        {loading ? (
          <p className="ac-loading">Chargement…</p>
        ) : logos.length === 0 ? (
          <p className="ac-empty">Aucun logo enregistré. Les logos statiques du site sont affichés par défaut.</p>
        ) : (
          <div className="ac-logos-grid">
            {logos.map(logo => (
              <div key={logo._id} className="ac-logo-item">
                <img src={logo.data} alt={logo.name} />
                <span className="ac-logo-name">{logo.name}</span>
                <button
                  className="ac-btn ac-btn--danger ac-btn--sm"
                  onClick={() => remove(logo._id)}
                >
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── FOIRE DE CANTON ───────────────────────────────────────────────────────────

const CF_DEFAULT = {
  visible: false,
  heroTitle: 'Foire de Canton 2026',
  heroLocation: 'Guangzhou, Chine',
  heroSubtitle: "La prochaine édition de la Foire de Canton à Guangzhou aura lieu à l'automne 2026 ! Inscrivez-vous dès maintenant et bénéficiez de l'accompagnement complet de GSSG.",
  phases: [
    { id: 'phase1', dates: '15 au 19 octobre 2026' },
    { id: 'phase2', dates: '23 au 27 octobre 2026' },
    { id: 'phase3', dates: '31 octobre au 4 novembre 2026' },
  ],
};

function CantonFairManager() {
  const [data, setData] = useState(CF_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: true });

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg({ text: '', ok: true }), 3500);
  };

  useEffect(() => {
    fetch(`${API}/canton-fair`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const changePhase = (idx, value) =>
    setData(prev => ({
      ...prev,
      phases: prev.phases.map((p, i) => i === idx ? { ...p, dates: value } : p),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/canton-fair`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      flash('Paramètres enregistrés avec succès.', true);
      window.dispatchEvent(new CustomEvent('cantonFairChanged', { detail: { visible: data.visible } }));
    } catch {
      flash('Erreur lors de la sauvegarde.', false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="ac-loading">Chargement…</p>;

  return (
    <div className="ac-section">
      <div className="ac-section__header">
        <div>
          <h3>Foire de Canton — Paramètres</h3>
          <p>Gérez la visibilité du bouton dans la navbar et le contenu de la page.</p>
        </div>
        <button className="ac-btn ac-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {msg.text && (
        <p className={`ac-msg ${msg.ok ? 'ac-msg--ok' : 'ac-msg--err'}`}>{msg.text}</p>
      )}

      {/* Visibility toggle */}
      <div className="ac-card">
        <div className="ac-card__head">
          <span className="ac-card__label">Visibilité dans la navigation</span>
        </div>
        <div className="ac-toggle-row">
          <div className="ac-toggle-info">
            <div className="ac-toggle-label">Afficher le bouton "Le Saviez-Vous ?" dans la navbar</div>
            <div className={`ac-toggle-hint ${data.visible ? 'ac-toggle-hint--on' : 'ac-toggle-hint--off'}`}>
              {data.visible
                ? '✅ Le bouton est visible pour tous les visiteurs.'
                : '⛔ Le bouton est masqué. La page reste accessible aux admins via URL directe.'}
            </div>
          </div>
          <label className="ac-toggle-switch">
            <input
              type="checkbox"
              checked={data.visible}
              onChange={e => setData(prev => ({ ...prev, visible: e.target.checked }))}
            />
            <span className="ac-toggle-slider" />
          </label>
        </div>
      </div>

      {/* Hero text */}
      <div className="ac-card">
        <div className="ac-card__head">
          <span className="ac-card__label">Texte du héro</span>
        </div>
        <div className="ac-form-col">
          <label className="ac-label">Titre principal</label>
          <input
            type="text"
            className="ac-input"
            value={data.heroTitle}
            onChange={e => setData(prev => ({ ...prev, heroTitle: e.target.value }))}
          />
          <label className="ac-label" style={{ marginTop: 12 }}>Localisation (sous le titre)</label>
          <input
            type="text"
            className="ac-input"
            value={data.heroLocation}
            onChange={e => setData(prev => ({ ...prev, heroLocation: e.target.value }))}
          />
          <label className="ac-label" style={{ marginTop: 12 }}>Texte de présentation</label>
          <textarea
            className="ac-input ac-textarea"
            rows={4}
            value={data.heroSubtitle}
            onChange={e => setData(prev => ({ ...prev, heroSubtitle: e.target.value }))}
          />
        </div>
      </div>

      {/* Phases dates */}
      <div className="ac-card">
        <div className="ac-card__head">
          <span className="ac-card__label">Dates des phases</span>
        </div>
        <div className="ac-form-col">
          {data.phases.map((phase, i) => (
            <div key={phase.id || i} className="ac-phase-row">
              <label className="ac-label">Phase {i + 1}</label>
              <input
                type="text"
                className="ac-input ac-input--grow"
                value={phase.dates}
                onChange={e => changePhase(i, e.target.value)}
                placeholder="Ex : 15 au 19 octobre 2026"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── PAGE PRINCIPALE ───────────────────────────────────────────────────────────

const TABS = [
  { key: 'chiffres',   label: 'Chiffres GSSG' },
  { key: 'logos',      label: 'Logos clients' },
  { key: 'cantonFair', label: '🏮 Foire de Canton' },
];

function AdminContent() {
  const [tab, setTab] = useState('chiffres');

  return (
    <div className="admin-content">
      <header className="admin-content__header">
        <h3>Gestion du contenu</h3>
        <p>Personnalisez les statistiques clés et les logos partenaires affichés sur le site.</p>
      </header>

      <div className="ac-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`ac-tab${tab === t.key ? ' ac-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'chiffres'   && <ChiffresManager />}
      {tab === 'logos'      && <LogosManager />}
      {tab === 'cantonFair' && <CantonFairManager />}
    </div>
  );
}

export default AdminContent;
